import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { ApiError } from '@/lib/api/client';
import { useT } from '@/lib/i18n';
import { describeError } from '@/lib/i18n/errors';

export function LoadingState({ label }: { label?: string }) {
  const t = useT();
  return (
    <div className="flex items-center gap-2 px-1 py-8 text-sm text-content-subtle">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {label ?? t('common.loading')}
    </div>
  );
}

/**
 * An empty screen states what belongs here, not merely that nothing does.
 * "Nothing found" leaves the user wondering whether it is broken.
 */
export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon?: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-2 px-1 py-10">
      {icon ? <div className="text-content-subtle">{icon}</div> : null}
      <p className="text-sm font-medium text-content">{title}</p>
      {hint ? <p className="max-w-md text-sm text-content-subtle">{hint}</p> : null}
      {action ? <div className="pt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const t = useT();
  // The API's own message is already specific ("username taken"); only the
  // generic fallback needs translating here. Codes that deserve better wording
  // are mapped in `describeError`.
  const message = error instanceof ApiError ? describeError(error) : t('common.error');

  return (
    <div
      className={cn(
        'rounded-md border border-overdue-border bg-overdue-subtle px-3.5 py-3',
        className,
      )}
      role="alert"
    >
      <p className="text-sm text-overdue">{message}</p>
      {onRetry ? (
        <button
          onClick={onRetry}
          className="mt-1.5 text-sm text-overdue underline underline-offset-4"
        >
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  );
}
